/**
 * The hub's raids (owner 2026-10-24: "raids of enemies running at the gates and them being killed … we can have the
 * fortress electrocute the enemies"), the part that needs no GL: when the next raid comes, who is in it, how they
 * run in, and the conductor's strike schedule that kills them all short of the camp gate. hubRaid.ts draws it.
 *
 * Pure (a random source is passed in), so test/hubRaidPlan.test.ts pins: the gaps between raids, the party's size and
 * mix, the hard cap on live raiders, that every raider dies before it reaches the gate, and that strikes never come
 * closer together than the conductor's recharge.
 */

/** The core raiders the hub's parties are made of (balance ids), and the occasional heavy and flyer. */
export type RaiderKind = "grunt" | "runner" | "shieldbearer" | "brute" | "flyer";

export const HUB_RAID = {
  /**
   * Seconds of quiet from one raid's last raider falling to the next raid setting off (uniform), and before the first
   * after the hub opens. (A raid itself runs ~12–16 s now that it walks out of the mist.)
   */
  every: [8, 15] as const,
  first: [2.5, 5] as const,
  /** Raiders a party (weights for 2, 3, 4, 5 and 6: small parties are the rule), and the core mix. */
  partyWeights: [3, 4, 3, 1.5, 1] as const,
  core: ["grunt", "grunt", "runner", "shieldbearer"] as readonly RaiderKind[],
  /** A party takes a brute (one), or a flyer or two, this often. */
  bruteChance: 0.25,
  flyerChance: 0.2,
  /** Live raiders at most, ever (a party is trimmed to it); on the low quality tiers, fewer. */
  cap: 6,
  capLow: 3,
  /** Seconds between raiders setting off (uniform: a loose column out of the mist), and their sideways spread (cells, ±). */
  stagger: [0.55, 1.15] as const,
  lane: 0.38,
  /**
   * Run speeds (cells/s) when the balance gives none, the hub's hurry on top of the balance's own speed, and the range
   * it is held to (a brute at its own pace took ~25 s down the road; a hurried runner outran the conductor).
   */
  fallbackSpeed: 1.1,
  hurry: 1.5,
  speed: [1.2, 2.4] as const,
  /**
   * The killing ground, as path distance before the gate (cells): the conductor opens up once the leader is `open`
   * from the gate, and nobody gets closer than `hold` (a strike is forced at the latest then). Now and then
   * (`closeCall`) the conductor waits a beat on the last raider standing, so it dies nearly at the gate.
   */
  open: 4.0,
  hold: 1.5,
  closeHold: 0.8,
  closeCall: 0.2,
  /** Seconds between two of the conductor's bolts at least, and the usual range. */
  minGap: 0.3,
  gap: [0.45, 0.8] as const,
  /** Bolts a brute takes (the first only staggers it). */
  bruteHits: 2,
  /**
   * The mist (owner 2026-10-24: "have them walk out of the mist at the top"): the path starts off the top of the
   * screen, and a raider dissolves in over `len` cells of its walk from `from` cells along it — while it crosses the
   * screen's top edge into the haze-softened far ground — so it never pops in (`mistFade`).
   */
  mist: { from: 0.8, len: 4.0 },
  /** The planner's time step (s). */
  step: 0.02,
} as const;

export interface RaiderPlan {
  readonly kind: RaiderKind;
  /** Seconds after the raid's start it sets off from the path's start. */
  readonly delay: number;
  /** Cells a second along the path. */
  readonly speed: number;
  /** Sideways offset from the road's centre line (cells; + is to its left). */
  readonly lane: number;
}

/** One bolt from the conductor: when (s after the raid's start), at whom, whether it is the full strike, whether it kills. */
export interface BoltPlan { readonly time: number; readonly raider: number; readonly strike: boolean; readonly lethal: boolean }

export interface RaidPlan { readonly raiders: readonly RaiderPlan[]; readonly bolts: readonly BoltPlan[] }

const between = (r: () => number, range: readonly [number, number]) => range[0] + r() * (range[1] - range[0]);

/** Seconds until the next raid. */
export function nextRaidIn(r: () => number, first = false): number { return between(r, first ? HUB_RAID.first : HUB_RAID.every); }

/** The party: 2–6 core raiders (weighted small), now and then a brute or flyers, never more than `cap`. */
export function rollParty(r: () => number, cap: number): RaiderKind[] {
  const w = HUB_RAID.partyWeights, total = w.reduce((a, b) => a + b, 0);
  let pick = r() * total, size = 2;
  for (let i = 0; i < w.length; i++) { if ((pick -= w[i]) < 0) { size = 2 + i; break; } }
  const out: RaiderKind[] = [];
  if (r() < HUB_RAID.bruteChance) out.push("brute");
  if (r() < HUB_RAID.flyerChance) { out.push("flyer"); if (r() < 0.5) out.push("flyer"); }
  while (out.length < size) out.push(HUB_RAID.core[Math.floor(r() * HUB_RAID.core.length) % HUB_RAID.core.length]);
  // the heavy walks in the middle of the pack, not at its head
  out.sort((a, b) => order(a) - order(b) || 0);
  return out.slice(0, Math.max(1, Math.min(cap, size)));
}
const order = (k: RaiderKind) => (k === "runner" ? 0 : k === "brute" ? 2 : 1);

/**
 * A whole raid: the party sets off down a path `length` cells long that ends at the gate, and the conductor's bolts,
 * found by running the raid forward: once the leader is in the killing ground the conductor fires at whoever is
 * nearest the gate, a recharge apart, and at once at anyone about to reach `hold` from it.
 */
export function planRaid(r: () => number, length: number, speedOf: (k: RaiderKind) => number, cap: number): RaidPlan {
  const kinds = rollParty(r, cap);
  let delay = 0;
  const raiders: RaiderPlan[] = kinds.map((kind, i) => {
    if (i > 0) delay += between(r, HUB_RAID.stagger);
    return { kind, delay, speed: Math.min(HUB_RAID.speed[1], Math.max(HUB_RAID.speed[0], speedOf(kind) * HUB_RAID.hurry)), lane: (r() * 2 - 1) * HUB_RAID.lane };
  });
  const closeCall = r() < HUB_RAID.closeCall;
  const hits = raiders.map((x) => (x.kind === "brute" ? HUB_RAID.bruteHits : 1));
  const dead = raiders.map(() => false);
  const bolts: BoltPlan[] = [];
  const at = (i: number, t: number) => Math.max(0, (t - raiders[i].delay) * raiders[i].speed);
  let last = -Infinity, next = -Infinity;
  const end = raiders[raiders.length - 1].delay + (length + 1) / Math.min(...raiders.map((x) => x.speed));
  for (let t = 0; t <= end && dead.some((d) => !d); t += HUB_RAID.step) {
    let lead = -1;
    for (let i = 0; i < raiders.length; i++) if (!dead[i] && (lead < 0 || at(i, t) > at(lead, t))) lead = i;
    const left = length - at(lead, t);
    const alive = dead.filter((d) => !d).length;
    const hold = closeCall && alive === 1 ? HUB_RAID.closeHold : HUB_RAID.hold;
    const forced = left <= hold + raiders[lead].speed * HUB_RAID.minGap;
    if (left > HUB_RAID.open || t < last + HUB_RAID.minGap) continue;
    const wait = closeCall && alive === 1 ? Infinity : next;
    if (t < wait && !forced) continue;
    hits[lead]--;
    const lethal = hits[lead] <= 0;
    if (lethal) dead[lead] = true;
    const n = bolts.length;
    bolts.push({ time: t, raider: lead, strike: boltIsStrike(n, raiders[lead].kind, lethal), lethal });
    last = t;
    next = t + between(r, HUB_RAID.gap);
  }
  return { raiders, bolts };
}

/**
 * Which bolts are the full strike (vfx.js `baseStrike`: pillar, shock rings, scorch, smoke column) and which the
 * zap (`baseZap`: one bolt and the tesla hit). Owner 2026-10-24: "Zap for most bolts" — a party's deaths were a wall
 * of pillars and smoke. The full strike is kept for two moments: the raid's opening bolt, and the bolt that fells a brute.
 */
export function boltIsStrike(n: number, kind: RaiderKind, lethal: boolean): boolean { return n === 0 || (kind === "brute" && lethal); }

/** How dissolved a raider is `s` cells along its path out of the mist: 1 hidden, 0 solid (smoothstep across HUB_RAID.mist). */
export function mistFade(s: number): number {
  const k = Math.min(1, Math.max(0, (s - HUB_RAID.mist.from) / HUB_RAID.mist.len));
  return 1 - k * k * (3 - 2 * k);
}

/** Where a raider is along the path at `t` s after the raid's start (cells; clamped to the path). */
export function raiderAt(p: RaiderPlan, t: number, length: number): number { return Math.min(length, Math.max(0, (t - p.delay) * p.speed)); }

/** A polyline the raiders follow: distance along it to a point and heading (pure; writes into `out`, allocates nothing). */
export class RaidPath {
  readonly length: number;
  private readonly cum: number[] = [0];

  /** `pts`: x, z pairs (world), from the far end to the gate. */
  constructor(private readonly pts: readonly (readonly [number, number])[]) {
    for (let i = 1; i < pts.length; i++) this.cum.push(this.cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    this.length = this.cum[this.cum.length - 1];
  }

  /** The point `s` along (clamped), shifted `lane` to the left of the way, and the way's unit direction. */
  sample(s: number, lane: number, out: { x: number; z: number; dx: number; dz: number }): void {
    const c = this.cum, p = this.pts;
    const d = Math.min(this.length, Math.max(0, s));
    let i = 1;
    while (i < c.length - 1 && c[i] < d) i++;
    const seg = c[i] - c[i - 1] || 1, k = (d - c[i - 1]) / seg;
    const dx = (p[i][0] - p[i - 1][0]) / seg, dz = (p[i][1] - p[i - 1][1]) / seg;
    out.dx = dx; out.dz = dz;
    out.x = p[i - 1][0] + (p[i][0] - p[i - 1][0]) * k - dz * lane;
    out.z = p[i - 1][1] + (p[i][1] - p[i - 1][1]) * k + dx * lane;
  }
}
